import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authFetch } from '../../api';
import { useGamificationContext } from '../../context/GamificationContext';
import { useThemeContext } from '../../context/ThemeContext';
import { 
  FiHome, 
  FiTarget, 
  FiTrendingUp, 
  FiPlus, 
  FiZap, 
  FiMenu, 
  FiX, 
  FiFlag, 
  FiDroplet, 
  FiSettings, 
  FiAward,
  FiClock,
  FiLogOut,
  FiShield,
  FiMoon,
  FiSun
} from 'react-icons/fi';

const Sidebar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { gamificationEnabled } = useGamificationContext();
  const { theme, toggleTheme } = useThemeContext();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: FiHome },
    { name: 'Log Session', href: '/session', icon: FiPlus },
    { name: 'Sessions', href: '/sessions', icon: FiClock },
    { name: 'Goals', href: '/goals', icon: FiFlag },
    { name: 'Techniques', href: '/techniques', icon: FiTarget },
    { name: 'Tournaments', href: '/tournaments', icon: FiShield },
    { name: 'Analytics', href: '/analytics', icon: FiTrendingUp },
    ...(gamificationEnabled ? [{ name: 'Achievements', href: '/achievements', icon: FiAward }] : []),
    { name: 'Hydration', href: '/hydration', icon: FiDroplet },
    { name: 'Settings', href: '/settings', icon: FiSettings }
  ];

  const closeSidebar = () => setIsOpen(false);

  const handleLogout = async () => {
    await authFetch('sign-out', { method: 'POST' });
    localStorage.removeItem('user_id');
    window.location.href = '/';
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-blue-900 dark:bg-slate-950 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <Link to="/" className="flex items-center space-x-2">
          <FiZap className="w-6 h-6 text-yellow-400" />
          <h1 className="text-lg font-bold">ClinchFirst</h1>
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="p-2 hover:bg-blue-800 dark:hover:bg-slate-800 rounded-lg transition-colors">
            {theme === 'dark' ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 hover:bg-blue-800 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {isOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-blue-900 dark:bg-slate-950 text-white transform transition-transform duration-300 ease-in-out flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:min-h-screen
        `}
      >
        <div className="flex-1 overflow-y-auto">
          {/* Desktop Header */}
          <div className="hidden lg:block p-6">
            <Link to="/" className="block hover:opacity-90 transition-opacity">
              <div className="flex items-center space-x-2">
                <FiZap className="w-8 h-8 text-yellow-400" />
                <h1 className="text-xl font-bold">ClinchFirst</h1>
              </div>
              <p className="text-blue-200 dark:text-slate-400 text-sm mt-1">Your Intelligent No-Gi Journal</p>
            </Link>
          </div>

          {/* Mobile Header Inside Sidebar */}
          <div className="lg:hidden p-6 border-b border-blue-800 dark:border-slate-800">
            <Link to="/" onClick={closeSidebar} className="block hover:opacity-90 transition-opacity">
              <div className="flex items-center space-x-2">
                <FiZap className="w-8 h-8 text-yellow-400" />
                <div>
                  <h1 className="text-xl font-bold">ClinchFirst</h1>
                  <p className="text-blue-200 dark:text-slate-400 text-xs mt-1">Your Intelligent No-Gi Journal</p>
                </div>
              </div>
            </Link>
          </div>

          <nav className="mt-4 lg:mt-8 pb-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeSidebar}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-800 dark:bg-slate-800 text-white border-r-4 border-yellow-400'
                      : 'text-blue-100 dark:text-slate-300 hover:bg-blue-800 dark:hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Bottom Actions */}
        <div className="p-6 border-t border-blue-800 dark:border-slate-800 mt-auto space-y-2">
          <button
            onClick={toggleTheme}
            className="hidden lg:flex items-center w-full px-4 py-2 text-sm font-medium text-blue-100 dark:text-slate-300 hover:bg-blue-800 dark:hover:bg-slate-800 hover:text-white transition-colors rounded-md"
          >
            {theme === 'dark' ? <FiSun className="w-5 h-5 mr-3" /> : <FiMoon className="w-5 h-5 mr-3" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>

          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-blue-100 dark:text-slate-300 hover:bg-blue-800 dark:hover:bg-slate-800 hover:text-white transition-colors rounded-md"
          >
            <FiLogOut className="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;